import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Building2, CalendarDays, Search, Upload, X, CheckCircle2 } from 'lucide-react';
import JobPortalHeader from '../components/JobPortal/JobPortalHeader';
import Footer from '../components/Footer';

const JobPortal: React.FC = () => {
    const [jobs, setJobs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { t, t_api } = useLanguage();
    
    // Search State
    const [searchTitle, setSearchTitle] = useState('');
    const [searchLevel, setSearchLevel] = useState('');
    const [searchLocationType, setSearchLocationType] = useState('');
    const [searchSkills, setSearchSkills] = useState('');
    const [filteredJobs, setFilteredJobs] = useState<any[]>([]);

    // Application Modal
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState<any>(null);
    const [applyForm, setApplyForm] = useState({
        name: '',
        email: '',
        phone: '',
        experience_years: '',
        education: '',
        current_location: '',
        expected_salary: '',
        notice_period: '',
        linkedin: '',
        cover_letter: ''
    });
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [applyStatus, setApplyStatus] = useState<'' | 'submitting' | 'success' | 'error'>('');

    const careerLevels = [
        "Entry", "Junior", "Associate", "Mid Level", "Senior", "Team Leader", 
        "Supervisor", "Manager", "Director", "Executive", "Expert", "Head", 
        "Lead", "Specialist", "Senior Specialist", "Engineer", "Analyst", "Architect"
    ];

    const locationTypes = ["On-site", "Remote", "Hybrid"];

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            const response = await fetch('/api/jobs');
            if (response.ok) {
                const data = await response.json();
                const activeJobs = data.filter((job: any) => job.status === 'Active');
                setJobs(activeJobs);
                setFilteredJobs(activeJobs);
            }
        } catch (err) {
            console.error("Failed to fetch jobs", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        let result = jobs;
        if (searchTitle) {
            result = result.filter(j => j.title.toLowerCase().includes(searchTitle.toLowerCase()));
        }
        if (searchLevel) {
            result = result.filter(j => j.careerLevel === searchLevel);
        }
        if (searchLocationType) {
            result = result.filter(j => j.locationType === searchLocationType);
        }
        if (searchSkills) {
            const qSkills = searchSkills.toLowerCase().split(',').map(s=>s.trim());
            result = result.filter(j => {
                if (!j.skills) return false;
                const jSkills = j.skills.toLowerCase();
                return qSkills.some(qs => jSkills.includes(qs));
            });
        }
        setFilteredJobs(result);
    };

    const handleApplyClick = (job: any) => {
        setSelectedJob(job);
        setShowApplyModal(true);
        setApplyStatus('');
        setApplyForm({ 
            name: '', 
            email: '', 
            phone: '',
            experience_years: '',
            education: '',
            current_location: '',
            expected_salary: '',
            notice_period: '',
            linkedin: '',
            cover_letter: ''
        });
        setResumeFile(null);
    };

    const submitApplication = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resumeFile) {
            alert("Please upload your resume.");
            return;
        }

        if (resumeFile.size > 2 * 1024 * 1024) {
            alert("Resume file size must be less than 2MB.");
            return;
        }

        setApplyStatus('submitting');
        const formData = new FormData();
        formData.append('jobId', selectedJob.id);
        formData.append('name', applyForm.name);
        formData.append('email', applyForm.email);
        formData.append('phone', applyForm.phone);
        formData.append('experience_years', applyForm.experience_years);
        formData.append('education', applyForm.education);
        formData.append('current_location', applyForm.current_location);
        formData.append('expected_salary', applyForm.expected_salary);
        formData.append('notice_period', applyForm.notice_period);
        formData.append('linkedin', applyForm.linkedin);
        formData.append('cover_letter', applyForm.cover_letter);
        if (resumeFile) formData.append('resume', resumeFile);

        try {
            const res = await fetch('/api/applications', {
                method: 'POST',
                body: formData
            });
            if (res.ok) {
                setApplyStatus('success');
                setTimeout(() => setShowApplyModal(false), 2000);
            } else {
                setApplyStatus('error');
            }
        } catch (err) {
            console.error(err);
            setApplyStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans pt-24">
            <JobPortalHeader />

            <main className="flex-1 max-w-7xl mx-auto w-full p-8 z-20 relative">
                {/* Search Bar / Filter Section */}
                <div className="bg-white rounded-2xl shadow-2xl p-8 mb-12 border-t-8 border-yellow-400 relative z-30">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-end">
                        <div className="w-full">
                            <label className="block text-[10px] font-black text-slate-400 mb-2 tracking-widest">{t('careers.search_placeholder').split('e.g.')[0] || 'Job Title / Keyword'}</label>
                            <div className="relative">
                                <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-red-600 w-5 h-5" />
                                <input 
                                    type="text" 
                                    placeholder={t('careers.search_placeholder')} 
                                    className="w-full border-b-2 border-slate-100 py-2 pl-8 focus:border-red-600 outline-none transition-all font-bold text-slate-800 bg-transparent text-sm"
                                    value={searchTitle}
                                    onChange={(e) => setSearchTitle(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="w-full">
                            <label className="block text-[10px] font-black text-slate-400 mb-2 tracking-widest">{t('careers.level')}</label>
                            <select 
                                className="w-full border-b-2 border-slate-100 py-2 focus:border-red-600 outline-none bg-transparent transition-all font-bold text-slate-800 text-sm"
                                value={searchLevel}
                                onChange={(e) => setSearchLevel(e.target.value)}
                            >
                                <option value="">{t('careers.all_levels')}</option>
                                {careerLevels.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>
                        <div className="w-full">
                            <label className="block text-[10px] font-black text-slate-400 mb-2 tracking-widest">{t('careers.skills')}</label>
                            <input 
                                type="text" 
                                placeholder={t('careers.search_placeholder')} 
                                className="w-full border-b-2 border-slate-100 py-2 focus:border-red-600 outline-none transition-all font-bold text-slate-800 bg-transparent text-sm"
                                value={searchSkills}
                                onChange={(e) => setSearchSkills(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="mt-8 flex justify-center">
                        <button 
                            onClick={handleSearch}
                            className="bg-red-600 hover:bg-black text-white px-10 py-3.5 rounded-xl font-black transition-all flex items-center justify-center gap-3 shadow-xl hover:shadow-red-600/20 active:scale-95 tracking-widest text-xs"
                        >
                            <Search className="w-4 h-4" /> {t('careers.search_button')}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20 text-gray-500">{t('careers.loading_jobs')}</div>
                ) : (
                    <div>
                        <div className="mb-10 text-center md:text-left">
                            <p className="text-red-600 font-black tracking-[0.2em] mb-4 uppercase text-[10px]">{t('careers.title')}</p>
                            <h3 className="text-2xl font-black text-black mb-6 uppercase tracking-wide flex items-center gap-4">
                                <span className="w-1.5 h-8 bg-red-600 rounded-full"></span>
                                {t('careers.vacancies')} <span className="text-slate-400 text-base font-bold">({filteredJobs.length})</span>
                            </h3>
                        </div>
                        
                        {filteredJobs.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-800">{t('careers.no_jobs')}</h3>
                                <p className="text-gray-500 mt-2">{t('careers.no_jobs_desc') || 'Try adjusting your filters or check back later.'}</p>
                                <button 
                                    onClick={() => {
                                        setSearchTitle(''); setSearchLevel(''); setSearchLocationType(''); setSearchSkills('');
                                        setFilteredJobs(jobs);
                                    }}
                                    className="mt-6 text-red-600 font-bold hover:underline"
                                >
                                    {t('careers.clear_filters')}
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {filteredJobs.map((job) => (
                                    <motion.div 
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        key={job.id} 
                                        className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-2xl hover:border-red-100 transition-all group relative overflow-hidden"
                                    >
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700" />
                                        
                                        <div className="flex justify-between items-start mb-6">
                                            <div>
                                                <h4 className="text-xl font-black text-slate-800 mb-2 group-hover:text-red-600 transition-colors tracking-tight">{t_api(job.title, job.title_ar)}</h4>
                                                <div className="flex items-center gap-3">
                                                    <span className="bg-slate-100 text-slate-600 px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest">
                                                        {t_api(job.type, job.type_ar)}
                                                    </span>
                                                    <span className="text-[10px] font-black text-red-600 tracking-widest">
                                                        {t_api(job.department, job.department_ar)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-4 mb-8">
                                            <div className="flex items-center gap-3 text-sm text-slate-500 font-bold">
                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                                                    <Briefcase className="w-4 h-4 text-red-600" />
                                                </div>
                                                {t_api(job.careerLevel, job.careerLevel_ar)}
                                            </div>
                                            <div className="flex items-center gap-3 text-sm text-slate-500 font-bold">
                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                                                    <MapPin className="w-4 h-4 text-red-600" />
                                                </div>
                                                {t_api(job.location, job.location_ar)} ({t_api(job.locationType, job.locationType_ar)})
                                            </div>
                                        </div>

                                        {job.skills && (
                                            <div className="mb-8 flex flex-wrap gap-2">
                                                {job.skills.split(',').map((skill: string, idx: number) => (
                                                    <span key={idx} className="bg-slate-50 text-slate-400 px-3 py-1 rounded-md text-[10px] font-black tracking-widest border border-slate-100">
                                                        {t_api(skill.trim())}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
                                            <span className="text-[10px] text-slate-400 font-black tracking-widest flex items-center gap-2">
                                                <CalendarDays className="w-3.5 h-3.5" /> {new Date(job.postedDate).toLocaleDateString()}
                                            </span>
                                            <button 
                                                onClick={() => handleApplyClick(job)}
                                                className="bg-yellow-400 hover:bg-black hover:text-white text-black px-8 py-3 rounded-xl font-black transition-all shadow-lg active:scale-95 tracking-widest text-xs"
                                            >
                                                {t('common.apply_now') || 'Apply Now'}
                                            </button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            <Footer />

            <AnimatePresence>
                {showApplyModal && selectedJob && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    >
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-3xl w-full max-w-4xl overflow-hidden shadow-2xl relative max-h-[90vh] flex flex-col"
                        >
                            <div className="bg-black px-10 py-8 flex justify-between items-center text-white border-b-8 border-red-600">
                                <div>
                                    <h3 className="text-2xl font-black tracking-tighter">Apply Now</h3>
                                    <p className="text-red-500 font-bold text-xs tracking-widest mt-1">{t_api(selectedJob.title, selectedJob.title_ar)} • {t_api(selectedJob.department, selectedJob.department_ar)}</p>
                                </div>
                                <button onClick={() => setShowApplyModal(false)} className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-red-600 transition-all group">
                                    <X className="w-6 h-6 text-white group-hover:rotate-90 transition-transform" />
                                </button>
                            </div>
                            
                            <div className="p-10 overflow-y-auto custom-scrollbar">
                                {applyStatus === 'success' ? (
                                    <div className="text-center py-20">
                                        <div className="w-24 h-24 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-red-600/20 animate-bounce">
                                            <CheckCircle2 className="w-12 h-12" />
                                        </div>
                                        <h4 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">Application Received</h4>
                                        <p className="text-slate-500 font-bold max-w-sm mx-auto leading-relaxed">Thank you for your interest in joining Hamdan Trading Group. Our recruitment team will review your profile and get back to you soon.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={submitApplication} className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Full Name *</label>
                                                <input 
                                                    type="text" required
                                                    value={applyForm.name} onChange={e=>setApplyForm({...applyForm, name: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Email Address *</label>
                                                <input 
                                                    type="email" required
                                                    value={applyForm.email} onChange={e=>setApplyForm({...applyForm, email: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Phone Number *</label>
                                                <input 
                                                    type="tel" required
                                                    value={applyForm.phone} onChange={e=>setApplyForm({...applyForm, phone: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Current Location *</label>
                                                <input 
                                                    type="text" required
                                                    value={applyForm.current_location} onChange={e=>setApplyForm({...applyForm, current_location: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                    placeholder="City, Country"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Highest Education *</label>
                                                <input 
                                                    type="text" required
                                                    value={applyForm.education} onChange={e=>setApplyForm({...applyForm, education: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                    placeholder="Degree, University"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Years of Experience *</label>
                                                <input 
                                                    type="text" required
                                                    value={applyForm.experience_years} onChange={e=>setApplyForm({...applyForm, experience_years: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                    placeholder="e.g. 5 Years"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Expected Salary</label>
                                                <input 
                                                    type="text" 
                                                    value={applyForm.expected_salary} onChange={e=>setApplyForm({...applyForm, expected_salary: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Notice Period</label>
                                                <input 
                                                    type="text" 
                                                    value={applyForm.notice_period} onChange={e=>setApplyForm({...applyForm, notice_period: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                    placeholder="e.g. 1 Month"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">LinkedIn Profile</label>
                                                <input 
                                                    type="url"
                                                    value={applyForm.linkedin} onChange={e=>setApplyForm({...applyForm, linkedin: e.target.value})}
                                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all"
                                                    placeholder="https://linkedin.com/in/username"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 mb-1">Upload Resume (PDF/DOCX, max 2MB) *</label>
                                                <div className="relative h-[52px]">
                                                    <input 
                                                        type="file" required accept=".pdf,.doc,.docx"
                                                        onChange={e => e.target.files && setResumeFile(e.target.files[0])}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    />
                                                    <div className="absolute inset-0 border-2 border-dashed border-slate-200 rounded-xl flex items-center px-4 bg-slate-50 group-hover:border-red-600 transition-colors">
                                                        <Upload className="w-4 h-4 text-slate-400 mr-3" />
                                                        <span className="text-xs font-bold text-slate-500 truncate">
                                                            {resumeFile ? resumeFile.name : "Click to upload resume"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 mb-1">Cover Letter / Message</label>
                                            <textarea 
                                                rows={4}
                                                value={applyForm.cover_letter} onChange={e=>setApplyForm({...applyForm, cover_letter: e.target.value})}
                                                className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-red-600 outline-none text-slate-900 font-bold transition-all resize-none"
                                                placeholder="Tell us why you're a great fit..."
                                            />
                                        </div>
                                        
                                        {applyStatus === 'error' && <p className="text-red-600 text-sm font-bold">An error occurred. Please try again.</p>}
                                        
                                        <div className="pt-4 flex justify-end gap-3">
                                            <button 
                                                type="button" onClick={() => setShowApplyModal(false)}
                                                className="px-6 py-2 font-black text-black border-2 border-black hover:bg-yellow-400 rounded-lg transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                type="submit" disabled={applyStatus === 'submitting'}
                                                className="bg-red-600 hover:bg-black text-yellow-400 px-8 py-2 rounded-lg font-black disabled:opacity-70 transition-colors shadow-lg"
                                            >
                                                {applyStatus === 'submitting' ? 'Submitting...' : 'Submit Application'}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default JobPortal;
